import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import {
  CreateAccountCommand,
  CreateAccountUserCommand,
  EventCountCommand,
} from '../commands/accounts/create.account.command';
import { ApiService } from '../common/services/api.service';
import { SpiderPage } from '../components/admin/accounts/event.spider/event.spider.modal.component';
import { ItemCount } from '../components/admin/accounts/show.places.count/show.places.count.modal.component';
import { Account, AccountUser } from '../models/admin/account';
import {
  EditedCategory,
  PlaceType,
} from '../components/hub/place.category/customize.place.category.component';
import {
  EventLocationFilterGroup,
  PlaceLocationFilterGroup,
} from '../components/hub/customization/location.filter.settings/location.filter.settings.provider';

@Injectable()
export class AccountApiService {
  constructor(private apiService: ApiService) {}

  public getAccounts(): Observable<Account[]> {
    return this.apiService.get('accounts');
  }

  public getAccountUsers(accountId: string): Observable<AccountUser[]> {
    return this.apiService.get(`accounts/${accountId}/users`);
  }

  public createAccountUsers(accountId: string, command: CreateAccountUserCommand): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/user`, command);
  }

  public deleteAccountUser(accountId: string, username: string): Observable<void> {
    return this.apiService.delete(`accounts/${accountId}/user/${username}`);
  }

  public createAccount(command: CreateAccountCommand): Observable<void> {
    return this.apiService.post('accounts', command);
  }

  public deleteAccount(accountId: string): Observable<void> {
    return this.apiService.delete(`accounts/${accountId}`);
  }

  public disableAccount(accountId: string): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/disable`);
  }

  public enableAccount(accountId: string): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/enable`);
  }

  public getIsClient(accountId: string): Observable<any> {
    return this.apiService.get(`accounts/${accountId}/is-client`);
  }

  public removePlaces(accountId: string): Observable<any> {
    return this.apiService.put(`accounts/${accountId}/remove-places`);
  }

  public getPlaceSource(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/place-source`);
  }

  public getIgVersion(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/ig-version`);
  }

  public setIgVersion(hubName: string, version: any): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/ig-version`, version);
  }

  public getTripPlannerSettings(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/trip-planner-settings`);
  }

  public setTripPlannerSettings(hubName: string, settings: any): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/trip-planner-settings`, settings);
  }

  public getTravelBuddyReport(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/travel-buddy-report`);
  }

  public getTravelBuddySettings(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/travel-buddy-settings`);
  }

  public setTravelBuddySettings(hubName: string, settings: any): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/travel-buddy-settings`, settings);
  }

  public createOpenAIAssistant(hubName: string, cityName: string, apikey: string): Observable<any> {
    return this.apiService.post(`accounts/${hubName}/openai-assistant`, {
      hubName,
      cityName,
      apikey,
    });
  }

  public uploadTravelBuddyFiles(hubName: string): Observable<any> {
    return this.apiService.post(`accounts/${hubName}/upload-travel-buddy-files`);
  }

  public deleteAssistant(hubName: string): Observable<any> {
    return this.apiService.delete(`accounts/${hubName}/openai-assistant`);
  }

  public getGooglePlaceBlockedCategories(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/settings`);
  }

  public getPlaceCount(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/place-count`);
  }

  public setGooglePlaceBlockedCategories(
    hubName: string,
    blockCategories: string
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/settings`, { blockCategories });
  }

  public setPlaceBlockedKeywords(hubName: string, blockKeywords: string[]): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/block-place-keywords`, {
      blockKeywords: blockKeywords.join(', '),
    });
  }

  public setCustomDefaultPlaceCategory(
    hubName: string,
    customDefaultPlaceCategory: PlaceType[],
    editedCategories: EditedCategory[]
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-categories`, {
      customDefaultPlaceCategory,
      editedCategories,
    });
  }

  public setUseDefaultPlaceCategory(hubName: string): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-use-default`);
  }

  public getCustomDefaultPlaceCategory(hubName: string): Observable<{
    useDefault: boolean;
    customCategories: PlaceType[];
    mergedDefaultCategories: { keys: string[]; values: string[] }[];
  }> {
    return this.apiService.get(`accounts/${hubName}/custom-place-categories`);
  }

  public setCustomPlaceGroups(
    hubName: string,
    customPlaceGroups: PlaceLocationFilterGroup[],
    isCityLevelFilter
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-location-filter-groups`, {
      customPlaceGroups,
      isCityLevelFilter,
    });
  }

  public setCustomEventGroups(
    hubName: string,
    customEventGroups: EventLocationFilterGroup[],
    isCityLevelFilter
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-event-location-filter-groups`, {
      customEventGroups,
      isCityLevelFilter,
    });
  }

  public setCustomPlaceUseGroupFilter(
    hubName: string,
    isCityLevelFilter: boolean
  ): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/custom-place-location-filter-city-level`, {
      useGroupFilter: isCityLevelFilter,
    });
  }

  public getCustomLocationFilterGroups(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/custom-location-filter-groups`);
  }

  public setPlaceSource(hubName: string, source: string, googleKey: string): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/place-source`, { source, googleKey });
  }

  // public setAreaBusiness(hubName: string, isAreaBusiness: boolean): Observable<any> {
  //     return this.apiService.put(`accounts/${hubName}/area-business`, {isAreaBusiness});
  // }

  public setBusinessType(hubName: string, businessType: string): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/business-type`, { businessType });
  }

  public setPlaceImagesShown(hubName: string, count: number): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/place-images-shown-count`, { count });
  }

  public getPlaceImagesShown(hubName: string): Observable<any> {
    return this.apiService.get(`accounts/${hubName}/place-images-shown-count`);
  }

  public setNearbyPlacesDefaultRange(hubName: string, range: number): Observable<any> {
    return this.apiService.put(`accounts/${hubName}/nearby-places-default-range`, { range });
  }

  public toggleIsClientAccount(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-is-client`, { status });
  }

  public setMenuBar(accountId: string, setToNormal: boolean): Observable<void> {
    if (setToNormal) {
      return this.apiService.put(`accounts/${accountId}/menu-bar-normal`);
    } else if (!setToNormal) {
      return this.apiService.put(`accounts/${accountId}/menu-bar-simple`);
    }
  }

  public SetDefaultApproved(
    accountId: string,
    eventApproved: boolean,
    placeApproved: boolean,
    jobApproved: boolean
  ): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/approved`, {
      eventApproved: eventApproved,
      placeApproved: placeApproved,
      jobApproved: jobApproved,
    });
  }

  public setContacts(accountId: string, contacts): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/set-contacts`, contacts);
  }

  public toggleTripPlanner(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-trip-planner`, { status });
  }

  public toggleCalendarViewLayout(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-calendar-view-layout`, { status });
  }

  public toggleItinerary(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-itinerary`, { status });
  }

  public togglePlaceCalendar(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-place-calendar`, { status });
  }

  public toggleEventCalendar(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-manual-events`, { status });
  }

  public toggleGoogleReviews(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-google-reviews`, { status });
  }

  public toggleFacebookPosts(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-facebook-posts`, { status });
  }

  public toggleNearbyPlacesRight(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-nearby-places-right`, { status });
  }

  public toggleEventByGeolocation(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-event-by-geolocation`, { status });
  }

  public toggleJobBoard(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-job-board`, { status });
  }

  public toggleRegionalAccountType(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-regional-account-type`, { status });
  }

  public togglePlaceCategory(accountId: string, placeCategory): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-place-category`, { placeCategory });
  }

  public togglePlaceByGeolocationTab(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-place-by-geolocation-tab`, { status });
  }

  public toggleNearbyEventsInPlace(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-nearby-events-in-place`, { status });
  }

  public toggleNearbyPlacesInEvent(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-nearby-places-in-event`, { status });
  }

  public togglePlaceLocationFilter(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-place-location-filter`, { status });
  }

  public toggleEventLocationFilter(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-event-location-filter`, { status });
  }

  public toggleEventTicketUrl(accountId: string, status: boolean): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/toggle-event-ticket-url`, { status });
  }

  public setTourLimitation(
    accountId: string,
    limit: {
      limit: { limited: boolean; limitation: number };
      googlePlaceGeofence: boolean;
      audioTour: boolean;
    }
  ): Observable<void> {
    return this.apiService.put(`accounts/${accountId}/tour-limitation`, limit);
  }

  public getToggles(
    accountId: string
  ): Observable<{ isDefaultPlaceCategory: boolean; toggle: any; tour: any }> {
    return this.apiService.get(`accounts/${accountId}/toggles`);
  }

  public getEventCount(accountId: string): Observable<EventCountCommand> {
    return this.apiService.get(`accounts/${accountId}/event-count`);
  }

  public getEventCronjobLastRunTime(accountId: string): Observable<EventCountCommand> {
    return this.apiService.get(`accounts/${accountId}/event-cronjob-last-run-time`);
  }

  public getEditorChoiceCount(accountId: string): Observable<{ eventChoice; placeChoice }> {
    return this.apiService.get(`accounts/${accountId}/editor-choice-count`);
  }

  public getItemCount(accountId: string): Observable<ItemCount> {
    return this.apiService.get(`accounts/${accountId}/account-item-count`);
  }

  public saveSpiderEvents(accountId: string, content): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/spider-events`, content);
  }

  public setSpiderEventPages(accountId: string, content: SpiderPage[]): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/spider-event-pages`, content);
  }

  public setEventPagesLimit(accountId: string, limit: number): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/event-pages-limit`, { limit });
  }

  public getEventPagesLimit(accountId: string): Observable<any> {
    return this.apiService.get(`accounts/${accountId}/event-pages-limit`);
  }

  public setIsAllowOnlineEvents(accountId: string, isAllowOnlineEvents: boolean): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/is-allow-online-events`, {
      isAllowOnlineEvents,
    });
  }

  public getIsAllowOnlineEvents(accountId: string): Observable<any> {
    return this.apiService.get(`accounts/${accountId}/is-allow-online-events`);
  }

  public getSpiderEventPages(
    accountId: string
  ): Observable<{ pagesMax: number; pages: SpiderPage[] }> {
    return this.apiService.get(`accounts/${accountId}/spider-event-pages`).do((x) => {
      x.pages.forEach((y) => {
        y.url = y.url.replace('?ref=page_internal', '');
      });
    });
  }

  public setEventTimezone(accountId: string, timezoneFormatter: string): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/event-pages-timezone`, {
      timezoneFormatter,
    });
  }

  public getEventTimezone(accountId: string): Observable<any> {
    return this.apiService.get(`accounts/${accountId}/event-pages-timezone`);
  }

  public getSerpBlockKeywords(): Observable<any> {
    return this.apiService.get(`accounts/serp-block-keywords`);
  }

  public setSerpBlockKeywords(
    blockKeywords: string[],
    account: { userName: string; isUseOwn: boolean }
  ): Observable<{ name; value }> {
    return this.apiService.put(`accounts/serp-block-keywords`, {
      account,
      blockKeywords: blockKeywords,
    });
  }

  public setApiKey(accountId: string): Observable<any> {
    return this.apiService.put(`accounts/${accountId}/api-key`);
  }

  public setRegionalAccounts(
    userName: string,
    regionalAccounts: string[],
    isSyncEventAfterAddingDMOAccounts: boolean
  ): Observable<any> {
    return this.apiService.put(`accounts/${userName}/regional-accounts`, {
      regionalAccounts: regionalAccounts,
      isSyncEventAfterAddingDMOAccounts,
    });
  }

  public getEventbriteEvents(accountId: string, eventIds: string[]) {
    return this.apiService.post(`accounts/${accountId}/get-eventbrite-events`, { eventIds });
  }

  public testPullEventbritePage(accountId: string, page: { url: string; tag?: string }) {
    return this.apiService.post(`accounts/${accountId}/test-pull-eventbrite-page`, { page });
  }

  public savePulledEventbriteEvents(
    accountId: string,
    events: any[],
    isApproved: boolean
  ): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/save-pulled-eventbrite-events`, {
      events,
      isApproved,
    });
  }

  public getJobKeywords(accountId: string): Observable<any> {
    return this.apiService.get(`accounts/${accountId}/job-keywords`);
  }

  public saveJobKeywords(
    accountId: string,
    keywords: { keyword: string; city: string; state: string; country: string }[]
  ): Observable<any> {
    return this.apiService.post(`accounts/${accountId}/job-keywords`, { keywords });
  }

  public proxyAccount(targetHubName: string): Observable<string> {
    return this.apiService.post('auth/proxy', { targetHubName });
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/ImGoing/IMGoing/web/src/services/account.api.service.ts